import {
  KanbanSquare,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import {
  useDataProvider,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

import type { Pipeline, Sale } from "../../types";
import { useActiveSales } from "../useActiveSales";
import { CardSheet, type CardSheetState } from "../card/CardSheet";
import {
  parseCardFilters,
  setSearchParam,
  toCardServerFilter,
} from "../filters";
import { useTagsByUsage } from "../useTagsByUsage";
import { useRefreshBoard } from "../useRefreshBoard";
import { BoardFilters } from "./BoardFilters";
import { PipelineBoard } from "./PipelineBoard";
import { PipelineDialog } from "./PipelineDialog";
import { usePipelines, usePipelineStages } from "./usePipelineData";

type PipelineDialogState =
  | { open: false }
  | { open: true; pipeline?: Pipeline };

export const PipelineView = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refreshBoard = useRefreshBoard();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pipelines, isPending: isPipelinesPending } = usePipelines();
  const [pipelineDialog, setPipelineDialog] = useState<PipelineDialogState>({
    open: false,
  });
  const [isDeletingPipeline, setIsDeletingPipeline] = useState(false);
  const [createInStage, setCreateInStage] = useState<Identifier | null>(null);

  const pipeline =
    pipelines.find(
      (candidate) => String(candidate.id) === searchParams.get("pipeline"),
    ) ?? pipelines[0];
  const { stages } = usePipelineStages(pipeline?.id);
  const { tags } = useTagsByUsage();
  const sales = useActiveSales();

  // The columns query the server with these filters, each one paginating
  const cardFilter = useMemo(
    () => ({
      ...toCardServerFilter(parseCardFilters(searchParams)),
      pipeline_id: pipeline?.id,
    }),
    [searchParams, pipeline?.id],
  );
  const tagsById = useMemo(
    () => new Map(tags.map((tag) => [tag.id, tag])),
    [tags],
  );
  const salesById = useMemo(
    () => new Map<Sale["id"], Sale>(sales.map((sale) => [sale.id, sale])),
    [sales],
  );

  const setParam = (key: string, value: string | null) =>
    setSearchParams((params) => setSearchParam(params, key, value), {
      replace: true,
    });

  const openCardId = searchParams.get("card");
  const sheetState: CardSheetState = createInStage
    ? { mode: "create", stageId: createInStage }
    : openCardId
      ? { mode: "view", cardId: openCardId }
      : { mode: "closed" };

  const handleSheetChange = (state: CardSheetState) => {
    setCreateInStage(state.mode === "create" ? state.stageId : null);
    setParam("card", state.mode === "view" ? String(state.cardId) : null);
  };

  if (isPipelinesPending) {
    return <Skeleton className="h-96 w-full" />;
  }

  const pipelineDialogElement = (
    <PipelineDialog
      open={pipelineDialog.open}
      pipeline={pipelineDialog.open ? pipelineDialog.pipeline : null}
      pipelines={pipelines}
      onClose={() => setPipelineDialog({ open: false })}
      onSaved={(id) => setParam("pipeline", String(id))}
    />
  );

  // The dialog keeps the same place in both branches, so creating the first
  // pipeline does not remount it
  if (!pipeline) {
    return (
      <>
        {pipelineDialogElement}
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <KanbanSquare className="size-10 text-muted-foreground" />
            <h2 className="text-lg font-semibold">
              {translate("crm.operations.pipelines.empty_title")}
            </h2>
            <p className="max-w-md text-sm text-muted-foreground">
              {translate("crm.operations.pipelines.empty")}
            </p>
            <Button onClick={() => setPipelineDialog({ open: true })}>
              <Plus />
              {translate("crm.operations.pipelines.create")}
            </Button>
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <>
      {pipelineDialogElement}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={String(pipeline.id)}
            onValueChange={(value) => setParam("pipeline", value)}
          >
            <SelectTrigger
              className="h-8 w-auto min-w-44 font-medium"
              aria-label={translate("crm.operations.pipelines.label")}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pipelines.map((candidate) => (
                <SelectItem key={candidate.id} value={String(candidate.id)}>
                  {candidate.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                aria-label={translate("crm.operations.pipelines.label")}
              >
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem
                onSelect={() => setPipelineDialog({ open: true })}
              >
                <Plus />
                {translate("crm.operations.pipelines.create")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setPipelineDialog({ open: true, pipeline })}
              >
                <Pencil />
                {translate("crm.operations.pipelines.rename")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={() => setIsDeletingPipeline(true)}
              >
                <Trash2 />
                {translate("crm.operations.pipelines.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <BoardFilters />
          <Button
            size="sm"
            className="ml-auto h-8"
            disabled={stages.length === 0}
            onClick={() => stages[0] && setCreateInStage(stages[0].id)}
          >
            <Plus />
            {translate("crm.operations.cards.add")}
          </Button>
        </div>

        <PipelineBoard
          pipelineId={pipeline.id}
          stages={stages}
          filter={cardFilter}
          tagsById={tagsById}
          salesById={salesById}
          onAddCard={setCreateInStage}
          onOpenCard={(card) => setParam("card", String(card.id))}
        />

        <CardSheet
          state={sheetState}
          onChange={handleSheetChange}
          pipelineId={pipeline.id}
          stages={stages}
        />
        <Confirm
          isOpen={isDeletingPipeline}
          title="crm.operations.pipelines.delete_title"
          titleTranslateOptions={{ name: pipeline.name }}
          content="crm.operations.pipelines.delete_description"
          onClose={() => setIsDeletingPipeline(false)}
          onConfirm={async () => {
            try {
              await dataProvider.delete("pipelines", {
                id: pipeline.id,
                previousData: pipeline,
              });
              setParam("pipeline", null);
              await refreshBoard();
              notify("crm.operations.pipelines.deleted");
            } catch (error: unknown) {
              notify(
                error instanceof Error
                  ? error.message
                  : "ra.notification.http_error",
                { type: "error" },
              );
            } finally {
              setIsDeletingPipeline(false);
            }
          }}
        />
      </div>
    </>
  );
};
